import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import { colors } from "../../theme/designSystem";
import {
  entityProfessionalTypeService,
  ProfTypeItem,
} from "../../services/entityProfessionalTypeService";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  available: ProfTypeItem[];
  onLinked: () => void;
}

const LinkProfessionalTypeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityId,
  available,
  onLinked,
}) => {
  const { toast, showToast, hideToast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      setSaving(false);
    }
  }, [isOpen]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    if (selected.size === 0) {
      showToast("Selecione ao menos um tipo de profissional.", "error");
      return;
    }
    setSaving(true);
    try {
      // O vinculo e por tipo; a API nao aceita lote, entao vinculamos em sequencia.
      for (const professionalTypeId of Array.from(selected)) {
        await entityProfessionalTypeService.link(entityId, professionalTypeId);
      }
      onLinked();
      onClose();
    } catch (error) {
      console.error("Erro ao vincular tipo profissional:", error);
      showToast("Erro ao vincular. Tente novamente.", "error");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
        }}
      >
        Vincular Tipos de Profissionais
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ color: "white" }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {available.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary, py: 2, textAlign: "center" }}
          >
            Todos os tipos de profissionais já estão vinculados a esta entidade.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {available.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                    sx={{
                      color: colors.primary,
                      "&.Mui-checked": { color: colors.primary },
                    }}
                  />
                }
                label={item.name}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || available.length === 0}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryHover },
          }}
        >
          Vincular
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default LinkProfessionalTypeModal;
